import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createReloginCode } from '#shared/api/auth';
import {
  archivePlayer,
  createPersonalInvite,
  createPlayer,
  getCrew,
  getInvites,
  getPlayer,
  listMembers,
  listPlayers,
  patchCrew,
  patchMember,
  patchPlayer,
  revokeInvite,
  rotateCrewCode,
  unarchivePlayer,
} from '#shared/api/players';
import { uuidv7 } from '#shared/core/id';
import type { PlayerDetail, PlayerListItem, PlayerPatch } from '#shared/schemas/player';
import { ME_KEY, useMe } from '@/app/session';
import { api } from '@/lib/api';
import { kvGet, kvSet } from '@/lib/idb';
import { outbox } from '@/lib/outbox';
import { queryClient } from '@/lib/query';
import { retryCreatesWaitingOnGuests } from './guestHeal';
import { createGuestQueue, type GuestStore, isOffline, type PendingGuest } from './pendingGuests';

/**
 * Roster queries. Every consumer shares the cache under the prefix ['players'], so one
 * invalidation (`queryClient.invalidateQueries({ queryKey: PLAYERS_KEY })`) refreshes them all:
 * the roster, one profile, the members list, the invites. Studio does exactly that after a card
 * is set active.
 */

export const PLAYERS_KEY = ['players'] as const;

export interface PlayersOpts {
  /** Default true: guests are on the court too. */
  includeGuests?: boolean;
  /** Default false. */
  includeArchived?: boolean;
}

export const playersKeys = {
  all: PLAYERS_KEY,
  list: (o: PlayersOpts = {}) =>
    ['players', 'list', { guests: o.includeGuests ?? true, archived: o.includeArchived ?? false }] as const,
  detail: (id: string) => ['players', 'detail', id] as const,
  members: ['players', 'members'] as const,
  invites: ['players', 'invites'] as const,
  crew: ['players', 'crew'] as const,
  games: (id: string) => ['players', 'games', id] as const,
  stats: (id: string) => ['players', 'stats', id] as const,
};

export async function fetchPlayers(o: PlayersOpts = {}): Promise<PlayerListItem[]> {
  const res = await api.call(listPlayers, {
    query: { includeGuests: o.includeGuests ?? true, includeArchived: o.includeArchived ?? false },
  });
  return res.items;
}

/** The roster. `players` is [] until the first answer (the persisted cache usually has one already). */
export function usePlayers(o: PlayersOpts = {}) {
  const q = useQuery({
    queryKey: playersKeys.list(o),
    queryFn: () => fetchPlayers(o),
    meta: { offline: true },
    staleTime: 60_000,
  });
  return { players: q.data ?? [], isLoading: q.isPending, isError: q.isError, refetch: q.refetch };
}

/** One roster entry by id, from the shared list (no extra request). */
export function usePlayerById(id: string | null | undefined): PlayerListItem | undefined {
  return usePlayers().players.find((p) => p.id === id);
}

/** The full profile of one player (member, coreCombineDone). */
export function usePlayer(id: string) {
  return useQuery<PlayerDetail>({
    queryKey: playersKeys.detail(id),
    queryFn: () => api.call(getPlayer, { params: { id } }),
  });
}

/** Who is looking: their player id and whether they own the crew. */
export function useViewer() {
  const me = useMe().data;
  return {
    memberId: me?.member.id ?? null,
    playerId: me?.player?.id ?? me?.member.playerId ?? null,
    isOwner: me?.member.role === 'owner',
    crewName: me?.crew.name ?? 'TTS',
    tz: me?.crew.tz ?? 'Europe/Athens',
  };
}

// ---------------------------------------------------------------- guests

const PENDING_KEY = 'players.pendingGuests';
let memoryPending: PendingGuest[] = [];
const guestStore: GuestStore = {
  async load() {
    try {
      return (await kvGet<PendingGuest[]>(PENDING_KEY)) ?? [];
    } catch {
      return [...memoryPending];
    }
  },
  async save(list) {
    memoryPending = [...list];
    try {
      await kvSet(PENDING_KEY, list);
    } catch {
      // No IndexedDB (private mode, tests): the memory copy still covers this session.
    }
  },
};

const postGuest = (g: PendingGuest) =>
  api.call(createPlayer, { body: { id: g.id, displayName: g.displayName, nickname: g.nickname, isGuest: true } });

export const guestQueue = createGuestQueue(guestStore, postGuest);

/** Ids of guests that were saved on this phone because there was no signal (this session). */
const queuedIds = new Set<string>();
export const wasQueued = (id: string): boolean => queuedIds.has(id);

/** Longest a caller waits on the network for queued guests. The send carries on in the background. */
export const GUEST_FLUSH_WAIT_MS = 4000;

/**
 * Send guests that were added offline. Games call it before they queue a game that has such a
 * guest in it (the server needs the player to exist). Safe to call any time, even with nothing
 * queued, and it never makes the caller wait long: on a dead connection it gives up after a few seconds.
 *
 * Offline it can send nothing, so it cannot order a guest before a game. What covers that case is
 * the step after a send: a game create the server refused only because its guest was not there yet
 * is put back in the outbox (see guestHeal.ts).
 */
export function flushPendingGuests(qc: QueryClient = queryClient): Promise<void> {
  const run = (async () => {
    const sent = await guestQueue.flush();
    if (sent.length === 0) return;
    await qc.invalidateQueries({ queryKey: PLAYERS_KEY });
    await retryCreatesWaitingOnGuests(sent, outbox);
  })().catch(() => {});
  let timer: ReturnType<typeof setTimeout> | undefined;
  const wait = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, GUEST_FLUSH_WAIT_MS);
  });
  return Promise.race([run, wait]).finally(() => clearTimeout(timer));
}

function optimisticGuest(g: PendingGuest): PlayerListItem {
  return {
    id: g.id,
    displayName: g.displayName,
    nickname: g.nickname,
    jersey: null,
    heightCm: null,
    hand: null,
    isGuest: true,
    memberId: null,
    archivedAt: null,
    activePortraitId: null,
    allowCrewPortraits: false,
    createdAt: g.createdAt,
    updatedAt: g.createdAt,
    portrait: null,
  };
}

function showGuestInCache(qc: QueryClient, g: PendingGuest): void {
  const item = optimisticGuest(g);
  qc.setQueriesData<PlayerListItem[]>(
    {
      predicate: (q) =>
        q.queryKey[0] === 'players' &&
        q.queryKey[1] === 'list' &&
        (q.queryKey[2] as { guests?: boolean } | undefined)?.guests === true,
    },
    (old) => (old && !old.some((p) => p.id === item.id) ? [...old, item] : old),
  );
}

export type GuestInput = string | { displayName: string; nickname?: string | null };

/**
 * Add a one-off guest (a cousin with no phone) mid-setup. Resolves to the new player's id. With
 * no signal the guest is kept on this phone, shows in the roster at once, and goes up later
 * (`wasQueued(id)` says which happened).
 */
export function useCreateGuest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: GuestInput) => {
      const displayName = (typeof input === 'string' ? input : input.displayName).trim();
      const nickname = (typeof input === 'string' ? null : input.nickname?.trim()) || null;
      const g: PendingGuest = { id: uuidv7(), displayName, nickname, createdAt: Date.now() };
      try {
        await postGuest(g);
      } catch (e) {
        if (!isOffline(e)) throw e;
        await guestQueue.add(g);
        queuedIds.add(g.id);
        showGuestInCache(qc, g);
      }
      return g.id;
    },
    onSuccess: (id) => {
      if (!queuedIds.has(id)) return qc.invalidateQueries({ queryKey: PLAYERS_KEY });
    },
  });
}

// ---------------------------------------------------------------- profile

/** Edit a card. Refreshes the roster, the profile and `me` (it carries member.displayName). */
export function usePatchPlayer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PlayerPatch) => api.call(patchPlayer, { params: { id }, body }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: PLAYERS_KEY }),
        qc.invalidateQueries({ queryKey: ME_KEY }),
        qc.invalidateQueries({ queryKey: ['stats'] }),
      ]);
    },
  });
}

export function useArchivePlayer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (archive: boolean) => api.call(archive ? archivePlayer : unarchivePlayer, { params: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAYERS_KEY }),
  });
}

// ---------------------------------------------------------------- crew admin

export function useCrew() {
  return useQuery({ queryKey: playersKeys.crew, queryFn: () => api.call(getCrew, {}), staleTime: 60_000 });
}

export function usePatchCrew() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.call(patchCrew, { body: { name } }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: playersKeys.crew }),
        qc.invalidateQueries({ queryKey: ME_KEY }),
      ]);
    },
  });
}

export function useMembers() {
  return useQuery({
    queryKey: playersKeys.members,
    queryFn: async () => (await api.call(listMembers, {})).items,
    staleTime: 30_000,
  });
}

export function usePatchMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; role?: 'owner' | 'member'; disabled?: boolean }) =>
      api.call(patchMember, { params: { id: v.id }, body: { role: v.role, disabled: v.disabled } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAYERS_KEY }),
  });
}

export function useInvites() {
  return useQuery({
    queryKey: playersKeys.invites,
    queryFn: () => api.call(getInvites, {}),
    staleTime: 15_000,
    // A code on a screen is not something to keep on disk.
    meta: { persist: false },
  });
}

export function useRotateCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.call(rotateCrewCode, { body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: playersKeys.invites }),
  });
}

/** A one-use invite for an existing player. The code is only in this answer. */
export function usePersonalInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (playerId: string) => api.call(createPersonalInvite, { body: { id: uuidv7(), playerId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: playersKeys.invites }),
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.call(revokeInvite, { params: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: playersKeys.invites }),
  });
}

/** "Get them back in": a 6-character code for a member, valid 15 minutes. */
export function useReloginCode() {
  return useMutation({
    mutationFn: (playerId: string) => api.call(createReloginCode, { body: { playerId } }),
    meta: { persist: false },
  });
}
